import React, { createContext, useState, useEffect } from "react";
import Api from "../api/api";
import UserAPI from "../api/UserApi";
import VendorAPI from "../api/VendorApi";
import { identifyUser, markInternalIfStaff, resetUser } from "../analytics";

export const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  const [globalState, setGlobalState] = useState({
    vendor: null,
    isAuthenticated: false,
    loading: true,
    isSuperAdmin: false,
    hasSuperAdminClaim: false,
    // Whether the authoritative super-admin check (custom claim + server vendor
    // doc) has finished. Admin-only routes stay blank rather than redirecting
    // until this is true, so a real admin is never bounced mid-resolution.
    // "The super-admin check completed successfully." Gates AUTHORIZATION —
    // useAuthRedirect bounces off /admin when this is true and isSuperAdmin is
    // false, so it must never be set on a path where we failed to determine
    // privileges.
    adminResolved: false,
    // "We are done trying, however it went." Gates DATA FETCHING only
    // (useEventsData). Set on success AND on failure, so a consumer waiting to
    // pick a query scope is never stuck, without that wait becoming an
    // authorization decision.
    adminSettled: false,
    vendorID: null,
    user: null,
    currentEventObj: null,
    needsProfileSetup: false,
  });

  useEffect(() => {
    const unsubscribe = Api.onAuthStateChanged(async (fbUser) => {
      try {
        if (fbUser) {
          // Try to get cached data from localStorage first
          const cachedVendor = localStorage.getItem("vendor")
            ? JSON.parse(localStorage.getItem("vendor"))
            : null;
          const cachedUser = localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user"))
            : null;

          // If we have cached data, set it immediately (non-blocking) for
          // fast first paint. Deliberately do NOT derive isSuperAdmin from the
          // cached vendor: localStorage is user-mutable, so trusting it here let
          // a tampered `vendor.isSuperAdmin` render the admin shell during the
          // window before fresh data arrives. isSuperAdmin/adminResolved are
          // left to prevState (false on a cold load) and set authoritatively by
          // fetchFreshData below, from the signed custom claim + server vendor doc.
          if (cachedVendor) {
            setGlobalState((prevState) => ({
              ...prevState,
              user: cachedUser,
              vendor: cachedVendor,
              isAuthenticated: true,
              loading: false,
              vendorID: cachedVendor.vendorID,
            }));
            // Identify early so a pageview fired during the warm window is
            // attributed rather than anonymous. Deliberately WITHOUT the vendor:
            // `isHost` is derived from it, and this cache is the same
            // user-mutable localStorage the isSuperAdmin comment above refuses
            // to trust. The fresh read below identifies again (idempotent per
            // uid) and is what actually sets the person property.
            identifyUser(cachedUser);
          }

          // Staff marking, from the ID token claim. Unawaited on purpose —
          // getIdTokenResult() can hit the network and nothing about signing in
          // should wait on an analytics label. This closes the coverage gap
          // taxonomy decision 9 recorded: before this, staff who only ever used
          // admin.gistirl.com were never marked internal anywhere.
          markInternalIfStaff(fbUser);

          // Fetch fresh data in the background
          const fetchFreshData = async () => {
            try {
              // GIS-228. These three reads have no interdependency, but they ran
              // strictly one after another, and nothing on /events could start
              // until all three landed — vendorID gates the list query. Started
              // together here; each is still awaited exactly where it was.
              //
              // The claim is kept as a separate promise with its own handler
              // rather than folded into the Promise.all: a transient token error
              // must still fall through to the server vendor flag below, whereas
              // a failed user or vendor read is genuinely fatal and belongs in
              // the outer catch, which is what Promise.all preserves.
              const claimPromise = fbUser.getIdTokenResult().then(
                (tokenResult) => tokenResult.claims.superAdmin === true,
                (claimError) => {
                  console.error("Error resolving super-admin claim:", claimError);
                  return false;
                }
              );
              const [fetchedUser, fetchedVendor] = await Promise.all([
                UserAPI.getUserById(fbUser.uid),
                VendorAPI.getVendorById(fbUser.uid),
              ]);

              // Ensure user profile exists first
              let user = fetchedUser;
              if (!user) {
                user = await UserAPI.createUser(fbUser);
              }
              // Hosts recruited via the become-a-host web form get a users doc
              // written as { name, photoURL } (setDoc-merge) with NO userID field,
              // and getUserById returns raw doc data — so trust the authed uid, not
              // the field. The doc is keyed by uid, so this is always correct, and
              // everything downstream (ProfileSetup save, event/coupon queries) reads
              // globalState.user.userID.
              user = { ...user, userID: fbUser.uid };
              localStorage.setItem("user", JSON.stringify(user));

              let vendor = fetchedVendor;

              // If vendor doesn't exist, create it
              if (!vendor) {
                vendor = await VendorAPI.createVendor(fbUser.uid);
              }
              // Same reason: adminReviewHostApplication provisions vendors/{uid}
              // without a vendorID field, so derive it from the uid.
              vendor = { ...vendor, vendorID: fbUser.uid };

              // Save fresh vendor to localStorage
              localStorage.setItem("vendor", JSON.stringify(vendor));
              localStorage.setItem("vendorID", vendor.vendorID);

              const needsProfileSetup = !user.name || !vendor?.meetupMemberID;

              // Authoritative admin gate: the signed superAdmin custom claim OR
              // the server-provisioned vendors/{uid}.isSuperAdmin field. This
              // mirrors the rules' isAdminOperator() (claim OR server-set flag) —
              // most operators hold only the flag, so requiring the claim alone
              // would lock them out. The flag is NOT client-writable (rules
              // block it in the vendors create/update affected() list), and the
              // vendor doc here is a fresh server read, so both signals are
              // trustworthy — unlike the localStorage cache we no longer trust.
              // Resolved above, in parallel with the two document reads. A
              // transient token error resolves to false rather than throwing, so
              // a flag-provisioned operator is still admitted by the vendor flag.
              const hasSuperAdminClaim = await claimPromise;
              const isSuperAdmin = hasSuperAdminClaim || vendor?.isSuperAdmin === true;

              // Update state with fresh data
              setGlobalState((prevState) => ({
                ...prevState,
                isSuperAdmin,
                // Distinct from isSuperAdmin: this is the signed custom claim ONLY.
                // Some callables (multi-event coupons, managing another host's
                // event) gate on the claim, not the vendors flag, so surface it so
                // the UI only offers those to callers who will actually pass.
                hasSuperAdminClaim,
                adminResolved: true,
                adminSettled: true,
                user,
                vendor,
                isAuthenticated: true,
                loading: false,
                vendorID: vendor.vendorID,
                needsProfileSetup,
              }));

              // Both docs are fresh server reads here, so this is the call that
              // may write `isHost`. Idempotent per uid: when the warm path above
              // already identified, this refreshes person properties instead of
              // sending a second $identify.
              identifyUser(user, vendor);
            } catch (error) {
              console.error("Error fetching fresh data:", error);
              // GIS-228. useEventsData waits on adminSettled before firing, so it
              // has to be set here or a transient getDoc failure leaves /events
              // on skeletons for the rest of the session with no error and no
              // retry (the cached-vendor path runs unawaited, after the shell has
              // already rendered).
              //
              // Deliberately NOT adminResolved, and deliberately NOT forcing
              // isSuperAdmin:false: useAuthRedirect bounces off /admin on
              // (adminResolved && !isSuperAdmin), so doing either here would eject
              // a real operator from the admin panel because one document read
              // failed. Privileges stay undetermined; only the wait ends.
              //
              // loading is cleared because the no-cachedVendor path awaits this
              // function and has nothing else to clear it — without this a cold
              // load that throws hangs on the spinner forever.
              setGlobalState((prevState) => ({
                ...prevState,
                adminSettled: true,
                loading: false,
              }));
            }
          };

          // If no cached data, we need to wait for fresh data
          if (!cachedVendor) {
            await fetchFreshData();
          } else {
            // Fetch fresh data in the background (non-blocking)
            fetchFreshData();
          }
        } else {
          localStorage.removeItem("vendor");
          localStorage.removeItem("user");
          localStorage.removeItem("vendorID");

          // Guarded internally on "was anyone actually identified in this
          // context": this branch also runs on an anonymous cold start, and an
          // unconditional reset there would mint a fresh distinct_id on every
          // page load.
          resetUser();

          setGlobalState((prevState) => ({
            ...prevState,
            isSuperAdmin: false,
            hasSuperAdminClaim: false,
            adminResolved: true,
            adminSettled: true,
            user: null,
            vendor: null,
            isAuthenticated: false,
            loading: false,
            vendorID: null,
          }));
        }
      } catch (error) {
        console.error("Authentication error:", error);
        // Reset state on error
        setGlobalState((prevState) => ({
          ...prevState,
          isSuperAdmin: false,
          hasSuperAdminClaim: false,
          // Auth itself threw, so there is no session: isAuthenticated is false
          // below and useAuthRedirect sends the user to "/" regardless. Both
          // flags are safe to set here, and both must be, so nothing waits.
          adminResolved: true,
          adminSettled: true,
          user: null,
          vendor: null,
          isAuthenticated: false,
          loading: false,
          vendorID: null,
        }));
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <GlobalContext.Provider value={{ globalState, setGlobalState }}>
      {children}
    </GlobalContext.Provider>
  );
};
